// src/hooks/useFileUpload.ts

import { useState } from 'react';

/**
 * 文件上传 Hook
 * 管理文件选择、上传状态和错误处理
 */
export const useFileUpload = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 设置要上传的文件
   * @param selectedFile - 用户选择的文件
   */
  const setUploadFile = (selectedFile: File | null): void => {
    setFile(selectedFile);
    setError(null); // 清除之前的错误
  };

  /**
   * 执行文件上传
   * @param uploadFn - 上传函数（接收 File 并返回 Promise）
   * @returns 上传结果
   */
  const uploadFile = async <T>(
    uploadFn: (file: File) => Promise<T>
  ): Promise<T | null> => {
    if (!file) {
      setError('请先选择一个文件');
      return null;
    }

    setUploading(true);
    setError(null);

    try {
      const result = await uploadFn(file);
      setFile(null); // 上传成功后清空文件
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '上传失败';
      setError(errorMessage);
      return null;
    } finally {
      setUploading(false);
    }
  };

  /**
   * 重置上传状态
   */
  const reset = (): void => {
    setFile(null);
    setUploading(false);
    setError(null);
  };

  return {
    file,
    uploading,
    error,
    setUploadFile,
    uploadFile,
    reset,
  };
};